import { useMemo } from "react";
import { useAdminPartnersList } from "../partners/use-admin-partners";
import { useAdminDeals } from "../deals/use-admin-deals";
import { useForms } from "../forms/use-forms";
import { useAdminSubmissions } from "../submissions/use-admin-submissions";

// Aggregates the four admin list hooks for the global-search palette. Each
// underlying query is gated on `open && hasPermission` so nothing fetches
// until the palette opens, and a section the user can't read never hits its
// (RBAC-gated) endpoint. The hooks share their cache keys with the real list
// pages, so opening the palette warms — or reuses — those caches for free.
//
// Permissions mirror the sidebar NAV gates: partners.read, deals.read,
// forms.read, submissions.read.

export type GlobalSearchPartner = {
  id: string;
  company_name: string;
  primary_contact_email: string | null;
};

export type GlobalSearchDeal = {
  hubspot_deal_id: string;
  deal_name: string | null;
  partner_names: string[];
};

export type GlobalSearchForm = {
  id: string;
  name: string;
  slug: string;
};

export type GlobalSearchSubmission = {
  id: string;
  form_name: string;
  partner_name: string | null;
  submitter_name: string | null;
};

export type GlobalSearchData = {
  partners: GlobalSearchPartner[];
  deals: GlobalSearchDeal[];
  forms: GlobalSearchForm[];
  submissions: GlobalSearchSubmission[];
  isLoading: boolean;
};

export function useGlobalSearchData(
  open: boolean,
  permissions: string[],
): GlobalSearchData {
  const can = (perm: string) => permissions.includes(perm);
  const wantPartners = open && can("partners.read");
  const wantDeals = open && can("deals.read");
  const wantForms = open && can("forms.read");
  const wantSubmissions = open && can("submissions.read");

  const partnersQuery = useAdminPartnersList({ enabled: wantPartners });
  const dealsQuery = useAdminDeals(false, null, { enabled: wantDeals });
  const formsQuery = useForms({ enabled: wantForms });
  const submissionsQuery = useAdminSubmissions({ enabled: wantSubmissions });

  const partners = useMemo<GlobalSearchPartner[]>(
    () =>
      (partnersQuery.data ?? []).map((p) => ({
        id: p.id,
        company_name: p.company_name,
        primary_contact_email: p.primary_contact_email,
      })),
    [partnersQuery.data],
  );

  // Flatten the kanban `grouped` (stage -> deals) into a single list.
  const deals = useMemo<GlobalSearchDeal[]>(() => {
    const grouped = dealsQuery.data?.grouped ?? {};
    return Object.values(grouped)
      .flat()
      .map((d) => ({
        hubspot_deal_id: d.hubspot_deal_id,
        deal_name: d.deal_name,
        partner_names: d.attributions.map((a) => a.partner_name),
      }));
  }, [dealsQuery.data]);

  const forms = useMemo<GlobalSearchForm[]>(
    () =>
      (formsQuery.data ?? []).map((f) => ({
        id: f.id,
        name: f.name,
        slug: f.slug,
      })),
    [formsQuery.data],
  );

  const submissions = useMemo<GlobalSearchSubmission[]>(
    () =>
      (submissionsQuery.data ?? []).map((s) => ({
        id: s.id,
        form_name: s.form_name,
        partner_name: s.partner_name,
        submitter_name: s.submitter_name,
      })),
    [submissionsQuery.data],
  );

  const isLoading =
    (wantPartners && partnersQuery.isPending) ||
    (wantDeals && dealsQuery.isPending) ||
    (wantForms && formsQuery.isPending) ||
    (wantSubmissions && submissionsQuery.isPending);

  return { partners, deals, forms, submissions, isLoading };
}
